import React, { useState, useEffect, useRef } from 'react';
import { useTx } from '../hooks/useTx';
import { useToast } from '../context/ToastContext';
import { useContracts } from '../hooks/useContract';
import { usd, fromUSDT } from '../utils/format';
import { PACKAGES } from '../config/contracts';
import IncomeChart from './common/IncomeChart';

export default function Dashboard({ data }) {
  const { run, pending } = useTx();
  const toast = useToast();
  const { evittaRead } = useContracts();

  const { userId, user, income, referrals, packages } = data;

  const claimable = income ? income.claimableAmount : (user ? user.claimableAmount : 0);
  const totalIncome = income ? income.totalIncome : 0;
  const totalClaimed = income ? income.totalClaimed : 0;
  const directs = user ? Number(user.totalDirectReferrals) : 0;
  const uplineId = user ? Number(user.referrer) : 0;
  const active = (packages || []).map((b) => !!b);

  // ---- Total team (whole downline) from the package-1 matrix ----
  const [teamSize, setTeamSize] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!evittaRead || !userId) return undefined;
    let cancelled = false;
    (async () => {
      const d = await evittaRead.getMatrixDetails(userId, 1).catch(() => null);
      if (!cancelled && mounted.current) setTeamSize(d ? Number(d.totalTeamSize) : 0);
    })();
    return () => { cancelled = true; };
  }, [evittaRead, userId]);

  // ---- Next package to unlock ----
  const nextIdx = active.findIndex((on, i) => !on && (i === 0 || active[i - 1]));
  const nextPkg = nextIdx >= 0 ? PACKAGES[nextIdx] : null;

  const withdraw = () =>
    run({ method: 'claimIncome', args: [], successMsg: 'Withdrawal sent to your wallet!', onDone: data.refresh });

  const upgradeNext = () =>
    run({
      method: 'upgradePackage',
      args: [nextPkg.id],
      approveAmount: nextPkg.price,
      successMsg: `Package ${nextPkg.id} activated!`,
      onDone: data.refresh,
    });

  const copyRef = async () => {
    const link = `${window.location.origin}${window.location.pathname}?ref=${userId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    } catch {
      toast.info(link);
    }
  };

  const hasClaim = claimable && fromUSDT(claimable) !== '0';

  return (
    <div className="tab fade-in">
      {/* 1 + 5 — User section: id, upline, avatar w/ animation, active packages */}
      <div className="card glow user-card" style={{ marginTop: 8 }}>
        <div className="avatar">
          <UserIcon />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Member #{userId}</div>
          <div className="uid-row">
            <span className="pill blue">Upline #{uplineId || '—'}</span>
            <span className="pill">{directs} direct</span>
          </div>
          <div className="pkg-dots">
            {PACKAGES.map((p, i) => (
              <span key={p.id} className={`pkg-dot ${active[i] ? 'on' : ''}`} title={`${p.name} ($${p.price})`}>
                {p.id}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Hero balance */}
      <div className="hero" style={{ marginTop: 14 }}>
        <div className="label">Available to Withdraw</div>
        <div className="big gold-text">{usd(claimable)}</div>
        <div className="uid">Member ID · #{userId}</div>
        <div style={{ marginTop: 16, position: 'relative' }}>
          <button className="btn" onClick={withdraw} disabled={pending || !hasClaim}>
            {pending ? <span className="spin-inline" /> : null}
            {pending ? 'Processing…' : 'Withdraw Income'}
          </button>
        </div>
      </div>

      {/* 2 + 4 — stats (USDT balance removed, total team added) */}
      <div className="grid-2" style={{ marginTop: 14 }}>
        <Stat label="Total Income" value={usd(totalIncome)} gold />
        <Stat label="Total Withdrawn" value={usd(totalClaimed)} />
        <Stat label="Total Team" value={teamSize == null ? '—' : teamSize} gold />
        <Stat label="Direct Team" value={directs} />
      </div>

      {/* 7 — upgrade next package */}
      {nextPkg && (
        <>
          <div className="section-title"><span className="bar" />Grow Your Account</div>
          <div className="card glow">
            <div className="card-row">
              <div>
                <div style={{ fontWeight: 700 }}>Unlock {nextPkg.name}</div>
                <div className="dim" style={{ fontSize: 12 }}>Package {nextPkg.id} · expands your ceiling</div>
              </div>
              <div className="stat-value gold" style={{ fontSize: 20 }}>${nextPkg.price}</div>
            </div>
            <div style={{ marginTop: 14 }}>
              <button className="btn" onClick={upgradeNext} disabled={pending}>
                {pending ? <span className="spin-inline" /> : null}
                {pending ? 'Processing…' : `Upgrade to Package ${nextPkg.id}`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 3 — referral link with copy */}
      <div className="section-title"><span className="bar" />Invite & Earn</div>
      <div className="card">
        <div className="card-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Your referral link</div>
            <div className="dim" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {`${window.location.host}${window.location.pathname}?ref=${userId}`}
            </div>
          </div>
          <button className="btn sm blue" onClick={copyRef} style={{ marginLeft: 10 }}>Copy</button>
        </div>
      </div>

      {/* 6 — income breakdown + graph */}
      <div className="section-title"><span className="bar" />Income Breakdown</div>
      <div className="card">
        <IncomeChart income={income} />
      </div>
      <div className="card" style={{ marginTop: 12 }}>
        <Line label="Sponsor Income" value={income?.sponsorIncome} />
        <Line label="Level Income" value={income?.levelIncome} />
        <Line label="Silver Pool" value={income?.silverPoolIncome} />
        <Line label="Gold Pool" value={income?.goldPoolIncome} />
        <Line label="Diamond Pool" value={income?.diamondPoolIncome} />
        <Line label="Special Bonus" value={income?.specialPoolIncome} last />
      </div>
    </div>
  );
}

function Stat({ label, value, gold }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${gold ? 'gold' : ''}`}>{value}</div>
    </div>
  );
}

function Line({ label, value, last }) {
  return (
    <div className="card-row" style={{ padding: '11px 0', borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.05)' }}>
      <span className="muted" style={{ fontSize: 13.5 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 14 }}>{usd(value)}</span>
    </div>
  );
}

function UserIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" fill="#1a1404" />
      <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" fill="#1a1404" />
    </svg>
  );
}
